import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InfoBox from "common-ui-components/components/info-box/InfoBox";
import StatTiles from "common-ui-components/components/stat-tiles/StatTiles";
import API from "common-ui-components/components/utils/API";
import styles from "./PatchOverviewPanel.module.css";

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const WEEKDAY_NUMBERS = {
  SUN: 1,
  MON: 2,
  TUE: 3,
  WED: 4,
  THU: 5,
  FRI: 6,
  SAT: 7,
};

const formatCount = (value) =>
  value === null || value === undefined
    ? "--"
    : Number(value).toLocaleString();

const formatPercent = (value) =>
  value === null || value === undefined
    ? "--"
    : `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

const formatPolicySchedule = (cronExpression) => {
  if (!cronExpression) return "";
  const [, , , dayOfMonth, , dayOfWeek] = cronExpression.trim().split(/\s+/);
  const isWildcard = (value) => value === "*" || value === "?";

  if (dayOfWeek && !isWildcard(dayOfWeek)) {
    const days = dayOfWeek.split(",").map((day) => {
      const dayNumber = /^\d+$/.test(day)
        ? Number(day)
        : WEEKDAY_NUMBERS[day.toUpperCase()];
      return WEEKDAYS[dayNumber - 1] ?? day;
    });
    return `Weekly, ${days.join(", ")}`;
  }
  if (dayOfMonth && !isWildcard(dayOfMonth)) {
    return `Monthly, Day ${dayOfMonth}`;
  }
  return "Daily";
};

const AutomatedPatchDeployment = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [nextPolicy, setNextPolicy] = useState(null);
  const [policyError, setPolicyError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await API.get("/devices/deployment/APDSummary");
      if (response?.status?.success !== true || !response?.body) {
        throw new Error(
          response?.status?.message || "Unable to load deployment summary.",
        );
      }
      setSummary(response.body);
      setNextPolicy(null);
      setPolicyError(false);

      if (response.body.nextScheduleAPDName) {
        try {
          const policiesResponse = await API.post(
            "/devices/get-automatic-patch-deployment-profiles?isOnlyPublished=false&page=0&size=150",
            {
              columns: [],
              sort: {},
              search: [],
              filter: [],
            },
          );
          if (
            policiesResponse?.status?.success !== true ||
            !Array.isArray(policiesResponse?.body?.result)
          ) {
            throw new Error(
              policiesResponse?.status?.message ||
                "Unable to load deployment policy details.",
            );
          }
          setNextPolicy(
            policiesResponse.body.result.find(
              (policy) =>
                policy.profileName === response.body.nextScheduleAPDName,
            ) ?? null,
          );
        } catch (policyFetchError) {
          console.error(
            "Failed to load next automated patch deployment policy details:",
            policyFetchError,
          );
          setPolicyError(true);
        }
      }
    } catch (fetchError) {
      console.error(
        "Failed to load automated patch deployment summary:",
        fetchError,
      );
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const activeCount = summary?.totalActivePolicies;
  const activePolicyCount = Number(activeCount ?? 0);
  const policyBadge = `${formatCount(activeCount)} active ${
    activePolicyCount === 1 ? "policy" : "policies"
  }`;
  const policyDetails = [
    summary?.nextScheduleAPDName,
    formatPolicySchedule(nextPolicy?.cronExpression),
    Array.isArray(nextPolicy?.patchSeverity)
      ? nextPolicy.patchSeverity
          .map((severity) => severity.charAt(0) + severity.slice(1).toLowerCase())
          .join(", ")
      : "",
  ].filter(Boolean);
  const tiles = [
    {
      label: "Patches deployed",
      value: loading ? "--" : formatCount(summary?.totalPatchesDeployed),
      color: "#111827",
    },
    {
      label: "Success rate",
      value: loading ? "--" : formatPercent(summary?.successRate),
      color:
        Number(summary?.successRate ?? 0) === 0 ? "#dc2626" : "#059669",
    },
  ];

  return (
    <InfoBox
      header="Automated Patch Deployment"
      subtitle="Scheduled patch deployment policies"
      badge={
        loading ? undefined : (
          <>
            <span className={styles.activePolicyDot} />
            {policyBadge}
          </>
        )
      }
      badgeProps={{
        style: {
          background: "#e2f7ed",
          color: "#047857",
        },
      }}
      style={{ minWidth: 0 }}
      childrenProps={{ className: styles.apdPanelContent }}
    >
      {error ? (
        <div className={styles.errorState} role="alert">
          <span>Unable to load deployment summary.</span>
          <button
            type="button"
            className={styles.retryButton}
            onClick={fetchSummary}
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <StatTiles
            items={tiles}
            columns={2}
            className={styles.apdStatTiles}
          />
          <div className={styles.nextRun}>
            <div>
              <strong>Next run:</strong>{" "}
              {summary?.nextScheduleTime || "Not scheduled"}
            </div>
            <div>
              <strong>Next policy:</strong>{" "}
              {summary?.nextScheduleAPDName}
            </div>
            <p>
              {/* {policyError
                ? "Unable to load policy schedule details."
                : policyDetails.length > 0
                  ? policyDetails.join(" · ")
                  : summary?.nextScheduleAPDName || "No upcoming deployment"} */}
            </p>
          </div>
        </>
      )}
      <button
        type="button"
        className={styles.actionLink}
        onClick={() => navigate("/app/secure/deployment/apd")}
      >
        Manage deployment policies <span aria-hidden="true">→</span>
      </button>
    </InfoBox>
  );
};

export default AutomatedPatchDeployment;
